/**
 * `resolveColumnSet` — the SSOT for de-prefixing, renaming, typing, and
 * documenting a virtual view's columns against ONE source CDF view.
 *
 * Every reshape/enrich/flatten factory feeds the same `keep` + `groups`
 * shapes through here so the column TYPES (derived from the source view's
 * parsed schema via `viewToSchema`) and the agent-facing `ColumnDoc`
 * docs (derived from `columnsFromView`, descriptions overridden by the
 * shape's canonical description) can never drift between factories.
 *
 * Source columns are CONSTRUCTED as `prefix + suffix` via `resolve`, never
 * matched by `startsWith` — the only collision-safe way to split a
 * scenario-prefixed source like `OptimizedRates_Network_String_Agent`.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';
import { columnsFromView, viewToSchema } from '#core/lib/cognite/view.ts';
import {
  arrayCol,
  type ColumnDoc,
  type ColumnType,
  nestedDatasetCol,
  rowIdentityCol,
  type Schema,
  schemaFromEntries,
  stringCol,
  structCol,
  timestampCol,
  tsRefInnerSchema,
  unknownCol,
  unwrapNullable,
  withUnit,
} from '#core/lib/expr/schema/types.ts';
import type { Cell, StructCell } from '#core/lib/expr/value.ts';
import { unitForSlug } from '#core/lib/units/registry.ts';

import { numericColForSlug, unitForKind } from './columnUnits.ts';
import type { EnrichGroup, Resolved, ResolvedFlat, Shape } from './shapes/index.ts';
import { isResolvedStruct, resolve } from './shapes/index.ts';

/**
 * Surface a relation column's target view into the engine `ColumnType` so the
 * reshaped view's declared schema renders relations as `reference<view>`. The
 * source's `viewToSchema` row-identity carries the target only when the
 * relation's `__typename` survived codegen; `columnsFromView` independently
 * recovers it as `referencedType`. When the declared `row-identity` (direct or
 * relation-list element) lacks a target, bake `referencedType` in; every other
 * `ColumnType` — and a row-identity that already names its target — passes
 * through unchanged.
 */
const withTargetView = (type: ColumnType, referencedType: string | undefined): ColumnType => {
  if (referencedType === undefined) {
    return type;
  }
  const surface: Partial<Record<ColumnType['kind'], () => ColumnType>> = {
    'row-identity': () =>
      type.kind === 'row-identity' && type.targetView === undefined
        ? rowIdentityCol(type.identity, referencedType)
        : type,
    array: () => (type.kind === 'array' ? arrayCol(withTargetView(type.element, referencedType)) : type),
  };
  return surface[type.kind]?.() ?? type;
};

/**
 * Refine a TimeSeries-ref column's nested-dataset INNER `value` type from the
 * domain-side typing rungs the source codegen could not express, KEEPING the
 * column a nested-dataset handle (replacing it with a bare scalar would break
 * per-cell minting). Non-nested-dataset columns pass through untouched.
 *
 *   - an explicit shape `valueType` is deterministic: `'date'` ⇒ unit-less
 *     `timestamp` (epoch-ms instants render as dates), `'string'` ⇒ unit-less
 *     `string`, `'number'` ⇒ `number` carrying the canonical name's unit;
 *   - no `valueType` and the inner value is still `unknown` (a
 *     discriminator-less `TimeSeries` ref) but the canonical name resolves to a
 *     unit ⇒ `number` carrying that unit (a declared unit is a reliable numeric
 *     signal). Otherwise the inner value stays `unknown`, resolved at fetch from
 *     the series' authoritative `isString` flag.
 */
const refineTsRefInner = (
  base: ColumnType,
  valueType: ResolvedFlat['valueType'],
  slug: ReturnType<typeof unitForKind>,
): ColumnType => {
  const unwrapped = unwrapNullable(base);
  if (unwrapped.kind !== 'nested-dataset') {
    return base;
  }
  const byValueType: Record<NonNullable<ResolvedFlat['valueType']>, () => ColumnType> = {
    date: () => timestampCol,
    string: () => stringCol,
    number: () => numericColForSlug(slug),
  };
  const resolveInner = valueType === undefined ? undefined : byValueType[valueType];
  if (resolveInner !== undefined) {
    return nestedDatasetCol(tsRefInnerSchema(resolveInner()));
  }
  const inner = unwrapped.innerSchema.get('value');
  if (inner?.kind === 'unknown' && slug !== undefined) {
    return nestedDatasetCol(tsRefInnerSchema(numericColForSlug(slug)));
  }
  return base;
};

/**
 * The resolved column set for a virtual view over `source`:
 *
 *   - `cols` — the source⇄canonical pairings (`keep` then each group), in
 *     order, used to drive both the rename expansion and the projection;
 *   - `declared` — the engine `Schema`, canonical name → the source
 *     column's derived `ColumnType` (`unknownCol` for an unknown source),
 *     a relation column's `row-identity` carrying its target view so the
 *     reshaped view renders it as `reference<view>`;
 *   - `columns` — the agent-facing `ColumnDoc[]`, each canonical name
 *     carrying the `declared` `ColumnType`, the source column's
 *     `referencedType`, and the shape's canonical description.
 */
export type ResolvedColumnSet = {
  readonly cols: readonly Resolved[];
  readonly declared: Schema;
  readonly columns: ColumnDoc[];
  /**
   * The SOURCE-named mint schema: the same unit-stamped column types as
   * `declared`, but keyed by source name (a struct explodes to its member
   * sources). A self-minting factory passes this to `instancesToColumns` so a
   * ts-ref column's inner `value` carries its slug unit at mint time — the raw
   * `viewToSchema(source)` types it `unknown`, minting UNBOXED cells that drop
   * the unit through downstream `time-bucket`/`unnest`.
   */
  readonly mintSchema: Schema;
};

/**
 * Resolve a view's `keep` + `groups` shapes into the engine schema + agent
 * docs the factories share. SSOT for de-prefix/rename/typing/docs/units.
 */
export const resolveColumnSet = (
  source: DataModelView,
  keep: Shape,
  groups?: readonly EnrichGroup[],
): ResolvedColumnSet => {
  const sourceSchema = viewToSchema(source);
  const sourceColumns = new Map(columnsFromView(source).map((c: ColumnDoc) => [c.name, c] as const));

  const cols: readonly Resolved[] = [
    ...resolve(keep),
    ...(groups ?? []).flatMap((group: EnrichGroup) => resolve(group.shape, group.prefix)),
  ];

  // Resolve one source column to its engine `ColumnType`, baking the resolved
  // unit hint (numeric leaves only; `withUnit` is a no-op otherwise) and
  // surfacing a relation's target view. `slug` is resolved from the column the
  // unit belongs to: the canonical name for a flat column, the struct canonical
  // for every member (max/min share their measure's unit).
  const typeOf = (source: string, slug: ReturnType<typeof unitForKind>): ColumnType => {
    const base = withTargetView(sourceSchema.get(source) ?? unknownCol, sourceColumns.get(source)?.referencedType);
    return slug === undefined ? base : withUnit(base, unitForSlug(slug));
  };

  // Resolve each column to its declared `ColumnType` + agent-facing
  // `referencedType`. A struct column assembles an engine `struct` from its
  // per-member source leaf types (each carrying the measure's unit) under one
  // canonical name; a flat column carries the source column's derived type.
  type ResolvedColumn = {
    readonly canonical: string;
    readonly description: string;
    readonly type: ColumnType;
    readonly referencedType?: string;
  };
  const resolved: readonly ResolvedColumn[] = cols.map((c: Resolved): ResolvedColumn => {
    if (isResolvedStruct(c)) {
      // Every struct member inherits the struct canonical's measurement category.
      const slug = unitForKind(c.kind);
      const fields = Object.fromEntries(
        Object.entries(c.members).map(
          ([member, source]: readonly [string, string]) => [member, typeOf(source, slug)] as const,
        ),
      );
      return { canonical: c.canonical, description: c.description, type: structCol(fields) };
    }
    const src = sourceColumns.get(c.source);
    const slug = unitForKind(c.kind);
    return {
      canonical: c.canonical,
      description: c.description,
      type: refineTsRefInner(typeOf(c.source, slug), c.valueType, slug),
      referencedType: src?.referencedType,
    };
  });

  // Engine schema: canonical name → the resolved ColumnType.
  const declared: Schema = schemaFromEntries(
    resolved.map(({ canonical, type }: ResolvedColumn) => [canonical, type] as const),
  );

  // SOURCE-named mint schema: the same unit-stamped types keyed by source name
  // (a struct explodes to its member sources) so `instancesToColumns` mints
  // BOXED ts-ref value cells — the raw source schema types the inner value
  // `unknown` and drops the unit.
  const mintSchema: Schema = schemaFromEntries(
    cols.flatMap((c: Resolved): readonly (readonly [string, ColumnType])[] => {
      const slug = unitForKind(c.kind);
      if (isResolvedStruct(c)) {
        return Object.values(c.members).map((source: string) => [source, typeOf(source, slug)] as const);
      }
      return [[c.source, refineTsRefInner(typeOf(c.source, slug), c.valueType, slug)] as const];
    }),
  );

  // Agent-facing columns: the canonical name carrying the declared ColumnType
  // (unit hint included), the source column's referencedType for relation/
  // timeseries rendering, and the shape's canonical description.
  const columns: ColumnDoc[] = resolved.map(
    ({ canonical, type, description, referencedType }: ResolvedColumn): ColumnDoc => ({
      name: canonical,
      type,
      description,
      ...(referencedType !== undefined && { referencedType }),
    }),
  );

  return { cols, declared, columns, mintSchema };
};
/**
 * The source columns a resolved column set reads from the source view: the
 * single source for a flat column, every member source for a struct column.
 * SSOT for the native-projection column list the enrich/flatten leaf providers
 * push into their CDF query.
 */
export const sourceColumnsOf = (cols: readonly Resolved[]): readonly string[] =>
  cols.flatMap((c: Resolved) => (isResolvedStruct(c) ? Object.values(c.members) : [c.source]));

/**
 * Assemble the canonical (agent-facing) column arrays from the source-projected
 * cell arrays. A flat column passes its source column through under the
 * canonical name; a struct column zips its member columns row-wise into one
 * `StructCell` per row. SSOT for the de-prefix/rename + struct-construction the
 * enrich/flatten leaf providers apply after the source read.
 */
export const assembleCanonicalColumns = (
  cols: readonly Resolved[],
  projected: Readonly<Record<string, readonly Cell[]>>,
): Record<string, readonly Cell[]> =>
  Object.fromEntries(
    cols.map((c: Resolved): readonly [string, readonly Cell[]] => {
      if (!isResolvedStruct(c)) {
        return [c.canonical, projected[c.source] ?? []];
      }
      const memberEntries: readonly (readonly [string, readonly Cell[]])[] = Object.entries(c.members).map(
        ([member, source]: readonly [string, string]) => [member, projected[source] ?? []] as const,
      );
      const rowCount = memberEntries.reduce(
        (n: number, [, cells]: readonly [string, readonly Cell[]]) => Math.max(n, cells.length),
        0,
      );
      const cells: readonly Cell[] = Array.from(
        { length: rowCount },
        (_: unknown, row: number): StructCell =>
          Object.fromEntries(
            memberEntries.map(([member, memberCells]: readonly [string, readonly Cell[]]) => [
              member,
              memberCells[row] ?? null,
            ]),
          ),
      );
      return [c.canonical, cells];
    }),
  );
